<script lang="ts">
import DOMPurify from 'dompurify'
import NrFormInput from '@nextrequestco/components/src/components/NrFormInput'
import NrIconButton from '@nextrequestco/components/src/components/NrIconButton'
import NrModal from '@nextrequestco/components/src/components/NrModal'
import NrIcon from '@nextrequestco/components/src/components/NrIcon'
import InfiniteLoading from 'v3-infinite-loading'
import API from '../api'
import DocumentCard from '../../pages/documents/components/DocumentSearchResult.vue'
import { isEmpty } from 'lodash'

export default {
  components: {
    NrFormInput,
    NrIconButton,
    NrModal,
    NrIcon,
    DocumentCard,
    InfiniteLoading
  },
  data() {
    return {
      modalSearchTerm: '',
      searchTerm: '',
      openDocListModal: false,
      filteredDocuments: [],
      modalHeaderTitle: '',
      infiniteId: +new Date(),
      infinitePage: 1,
      infiniteState: {},
      pageSize: 50
    }
  },
  methods: {
    sanitize(content: string) {
      return DOMPurify.sanitize(content, { ALLOWED_TAGS: ['em'] })
    },

    triggerInfiniteHandler($state): void {
      this.infinitePage += 1
      this.infiniteState = $state
      const payload = {
        search_term: this.searchTerm,
        page_number: this.infinitePage,
        page_size: this.pageSize
      }
      API.fetchDocumentList(payload)
        .then((response) => {
          const documents = response.data.documents
          if (documents.length < this.pageSize) {
            $state.complete()
          } else {
            $state.loaded()
          }
          if (documents.length) {
            this.filteredDocuments = this.filteredDocuments.concat(documents)
          }
        })
        .catch(() => {
          this.$toasted.error('An error occured. Please try again.')
        })
    },
    submitFullTextSearch(): void {
      if (!this.searchTerm) return
      this.infinitePage = 1
      const payload = {
        search_term: this.searchTerm,
        page_number: this.infinitePage,
        page_size: this.pageSize
      }
      this.modalHeaderTitle = `Loading search results for "${this.searchTerm}"`
      this.openDocListModal = true
      API.fetchDocumentList(payload)
        .then((response) => {
          const documents = response.data.documents
          const totalCount = response.data.total_count
          if (documents.length <= this.pageSize) {
            this.filteredDocuments = documents
            if (!isEmpty(this.infiniteState)) {
              this.infiniteId = this.infiniteId + 1
            }
          } else {
            this.filteredDocuments = []
            if (!isEmpty(this.infiniteState)) {
              this.infiniteState.complete()
            }
          }
          this.modalSearchTerm = this.searchTerm
          this.modalHeaderTitle = `${totalCount} Search results for "${this.modalSearchTerm}"`
        })
        .catch(() => {
          this.$toasted.error('An error occured. Please try again.')
        })
    }
  }
}
</script>

<template>
  <div class="search-doc-container">
    <NrFormInput
      name="document-search"
      class="search-doc-text-field"
      placeholder="Search documents by keyword"
      hideLabel
      label="Search documents by keyword"
      @keypress.enter="submitFullTextSearch"
      v-model="searchTerm"
    />

    <NrIconButton
      class="search-doc-text-btn"
      variant="bordered-neutral"
      aria-label="Search documents by text keyword"
      :icon="{ name: 'search', type: 'regular' }"
      @click="submitFullTextSearch"
    />
    <NrModal
      showClose
      :title="modalHeaderTitle"
      :show="openDocListModal"
      :showFooter="false"
      @on-close-click="openDocListModal = false"
    >
      <template v-slot:header>
        <h2 class="nr-modal__title nr-modal__title--hidden-footer">
          {{ modalHeaderTitle }}
        </h2>
        <div class="modal-doc-text-field">
          <NrFormInput
            name="document-search"
            class="search-doc-text-field"
            placeholder="Search documents by keyword"
            hideLabel
            label="Search documents by keyword"
            @keypress.enter="submitFullTextSearch"
            v-model="searchTerm"
          />
          <NrIconButton
            class="search-doc-text-btn"
            variant="bordered-primary"
            :icon="{ name: 'search', type: 'regular' }"
            aria-label="Search documents by text keyword"
            @click="submitFullTextSearch"
          />
        </div>
      </template>

      <DocumentCard
        v-for="(doc, index) in filteredDocuments"
        :key="`${doc.id}-item-${index}`"
        :header="doc.created_at"
        :title="doc.title"
        :url="doc.document_path"
        :visibility="doc.state"
        :filetype="doc.file_extension"
      >
        <template v-slot:content>
          <span v-html="sanitize(doc.highlights.content)"></span>
        </template>
      </DocumentCard>
      <InfiniteLoading
        :identifier="infiniteId"
        @infinite="triggerInfiniteHandler"
      >
        <template v-slot:spinner>
          <NrIcon name="spinner" class="full-text-search-loading-icon" />
        </template>
        <template v-slot:complete>
          <div class="no-more-results">
            <div v-if="filteredDocuments.length && modalSearchTerm.length">
              All documents loaded matching your search term "{{
                modalSearchTerm
              }}"
            </div>
            <div v-else>No documents found matching your search term</div>

            <div v-if="!modalSearchTerm.length" class="no-more-results">
              All documents loaded
            </div>
          </div>
        </template>
      </InfiniteLoading>
    </NrModal>
  </div>
</template>

<style lang="scss">
@import '../../../styles/base/_variables.scss';
@import '@nextrequestco/components/src/styles/tokens/tokens.scss';

@media screen and (max-width: 1137px) {
  .search-field-below-navbar {
    display: flex;
    margin-left: 12px;
    padding-bottom: 16px;
  }
  .search-field-on-navbar {
    display: none;
  }
}

@media screen and (min-width: 1138px) {
  .search-field-below-navbar {
    display: none;
  }
  .search-field-on-navbar {
    display: flex;
  }
}

.search-doc-container {
  .search-doc-text-field.field {
    min-width: 302px;
    margin-bottom: 0;
  }
  .search-doc-text-btn {
    margin-left: 6px;
    margin-bottom: 0;
  }
  .full-text-search-loading-icon {
    margin: auto;
    font-size: $spacing-32;
  }
}
.search-doc-text-field {
  input.input {
    background-color: white;
    border-color: #6e6e87;
    border-radius: 4px;
    color: #30303b;
    margin: 0;
    font-size: 1rem;
    height: 2.25rem;
    padding: 8px;
  }
}
.search-doc-container {
  .nr-modal {
    .nr-modal__container {
      text-transform: none;
    }
  }
  .nr-modal__content {
    height: 500px;
    padding-top: 0;

    span {
      em {
        background-color: rgba(#3f53da, 0.25);
        font-style: normal;
        color: $black;
      }
    }
  }
  .no-more-results {
    margin-top: 16px;
    color: #6e6e87;
    size: 20px;
  }
  .modal-doc-text-field {
    display: flex;
    margin-top: 1em;

    input.input {
      width: 450px;
    }
  }
}
</style>
